import { RefObject, MutableRefObject } from 'react';
import { TypesetterWorkerClient } from '../../../../workers/TypesetterWorkerClient';
import type { SelectionRange } from '../../EdgeDocsTypes';
import { executeWasmErrorRecovery, resetErrorCounter } from '../../../../rendering/recovery';

// ============================================================================
// DEBUG CONTROL: Set to false to disable all debug logging in production
// ============================================================================
const DEBUG_ENABLED = false; // ✅ DISABLED: Rapid typing performance fix
const debugLog = DEBUG_ENABLED ? console.log.bind(console) : () => {};

// ============================================================================
// PERFORMANCE BENCHMARK: Option C Assessment
// Set to true to measure WASM round-trip time for every keystroke
// This helps determine if Option C (WASM as SSOT) is viable
// ============================================================================
const BENCHMARK_WASM_LATENCY = true; // ✅ ENABLED: Measuring WASM round-trip for Option C assessment
const perfLog = BENCHMARK_WASM_LATENCY 
  ? (label: string, ms: number) => console.log(`⏱️ [PERF] ${label}: ${ms.toFixed(2)}ms`)
  : () => {};

export interface TextEditingHandlerDependencies {
  // Core refs (MutableRefObject for refs that need to be written)
  typesetterClientRef: RefObject<TypesetterWorkerClient | null>;
  currentDocumentTextRef: MutableRefObject<string>;
  currentSnapshotRef: RefObject<any>;
  lastSelectionRef: MutableRefObject<SelectionRange | null>;
  incrementalPagesRef: RefObject<any>;
  lastRenderWasIncrementalRef: MutableRefObject<boolean>;
  forceReinitRef: MutableRefObject<boolean>;
  consecutiveWasmErrorsRef: MutableRefObject<number>;
  lastLocalTextUpdateRef: MutableRefObject<string | null>;
  // ✅ PHASE 1: Removed textPropagationTimeoutRef - no longer needed without debouncing

  // Overlay refs
  overlaySettingsRef: RefObject<any>;

  // State setters
  setRenderTrigger: (updater: (prev: number) => number) => void;
  setStableInputText: (updater: (prev: string) => string) => void;
  setTypesetterClient: (client: TypesetterWorkerClient | null) => void;
  setIsTypesetterReady: (ready: boolean) => void;

  // Props and config
  documentState: { text: string; pageSetup?: any; fontSettings?: any } | null | undefined;
  fontFamily: string;
  onDocumentTextChange?: (newText: string) => void;

  // Utility functions
  debugLog: (...args: any[]) => void;
  incrementalDebugLog: (category: 'incremental' | 'delta' | 'snapshot' | 'general', message: string, data?: any) => void;
  renderOverlayRef: RefObject<(() => void) | null>;
  drawOverlaysRef: RefObject<(() => void) | null>;
  
  // ✅ OPTION C: When enabled, skip WASM processing (handled by applyEditCommand instead)
  optionCEnabled?: boolean;
}

/**
 * Handles complex text editing logic with incremental layout support.
 * Processes text changes, manages incremental updates, and handles WASM synchronization.
 */
export function createTextEditingHandler({
  typesetterClientRef,
  currentDocumentTextRef,
  currentSnapshotRef,
  lastSelectionRef,
  incrementalPagesRef,
  lastRenderWasIncrementalRef,
  forceReinitRef,
  consecutiveWasmErrorsRef,
  lastLocalTextUpdateRef,
  // ✅ PHASE 1: Removed textPropagationTimeoutRef parameter
  overlaySettingsRef,
  setRenderTrigger,
  setStableInputText,
  setTypesetterClient,
  setIsTypesetterReady,
  documentState,
  fontFamily,
  onDocumentTextChange,
  debugLog,
  incrementalDebugLog,
  renderOverlayRef,
  drawOverlaysRef,
  optionCEnabled = false
}: TextEditingHandlerDependencies) {
  return async (newText: string, selection: SelectionRange) => {
    // ✅ OPTION C: When enabled, WASM processing is handled by applyEditCommand
    // This handler ONLY updates refs - NO render trigger!
    // 
    // ⚠️ CRITICAL: Do NOT call setRenderTrigger here!
    // Triggering a render will cause the Canvas to call initializeDocument with the
    // optimistic text, which wipes WASM's paragraph structure. The queued edit command
    // then fails with "No paragraph found at position X" because WASM was re-initialized.
    //
    // The render is triggered by handleEditCommand AFTER WASM confirms the change.
    // This ensures WASM's state is consistent when the render happens.
    if (optionCEnabled) {
      // ⚠️ CRITICAL FIX: Do NOT update currentDocumentTextRef here!
      // The ref must stay in sync with WASM's lastInitializedText.
      // If we update the ref optimistically before WASM confirms, click-to-grapheme
      // resolution will use stale snapshot data while the ref has new text,
      // causing delete operations to target the wrong character.
      //
      // Only update lastSelectionRef for caret positioning.
      // handleEditCommand will update currentDocumentTextRef AFTER WASM confirms.
      lastSelectionRef.current = selection;
      
      // ⚠️ DO NOT propagate to parent or trigger render here!
      // handleEditCommand will do both AFTER WASM confirms the change.
      
      return;
    }
    // 🚀 CRITICAL FIX: The selection parameter is the NEW selection (after insertion)
    // For insertions, we need to calculate the OLD selection by subtracting the length difference
    // For deletions, the selection is already at the correct position (after deletion)

    debugLog('📝 [CanvasV3] Text changed:', {
      newTextLength: newText.length,
      selection,
      propagatingToParent: !!onDocumentTextChange
    });

    // 🚀 INCREMENTAL LAYOUT: Try incremental update first
    // ✅ CRITICAL FIX: Use WASM's known text as oldText, not the Canvas's ref
    // The Canvas's ref can be ahead of WASM if keystrokes accumulated faster than WASM could process them
    // Using the ref directly causes "No paragraph found at position X" errors when out of sync
    const canvasRefText = currentDocumentTextRef.current;
    const wasmKnownText = typesetterClientRef.current?.getLastInitializedText?.() || null;
    
    // Use WASM's known text if available, otherwise fall back to Canvas ref
    // This ensures we're comparing against what WASM actually has, not what the Canvas thinks it has
    const oldText = wasmKnownText !== null && wasmKnownText !== undefined 
      ? wasmKnownText 
      : canvasRefText;

    // 🔍 DEBUG: Log old text details to diagnose text disappearing issue
    DEBUG_ENABLED && console.log('🔍 [Phase3 Debug] Text state on edit:', {
      canvasRefTextLength: canvasRefText?.length || 0,
      wasmKnownTextLength: wasmKnownText?.length || 0,
      oldTextLength: oldText?.length || 0,
      oldTextPreview: oldText?.substring(0, 50) || '(empty)',
      newTextLength: newText.length,
      newTextPreview: newText.substring(0, 50),
      documentStateTextLength: documentState?.text?.length || 0,
      documentStateTextPreview: documentState?.text?.substring(0, 50) || '(empty)',
      canvasAheadOfWasm: canvasRefText && wasmKnownText ? canvasRefText.length !== wasmKnownText.length : false
    });

    incrementalDebugLog('incremental', 'Starting incremental layout check', {
      hasClient: !!typesetterClientRef.current,
      hasDocumentText: !!oldText,
      oldTextLen: oldText?.length || 0,
      newTextLen: newText.length,
      wasmKnownTextLen: wasmKnownText?.length || 0
    });

    let incrementalLayoutSucceeded = false;

    if (typesetterClientRef.current && oldText !== null && oldText !== undefined) {
      incrementalDebugLog('incremental', 'Conditions met, attempting incremental layout');

      try {

        // Calculate change bounds for incremental layout
        // ✅ FIX: For deletions (backspace/delete), use CURRENT selection (where cursor is NOW)
        // For insertions, calculate OLD selection by subtracting length difference from NEW selection
        const lengthDiff = newText.length - oldText.length;
        let changeStart: number;
        let changeEnd: number;

        if (lengthDiff < 0) {
          // ✅ DELETION: Use current selection position (where cursor is after delete)
          // Backspace: cursor moves left, so current position is START of deleted region
          changeStart = Math.min(selection.anchor, selection.focus);
          changeEnd = changeStart - lengthDiff; // lengthDiff is negative, so this adds
          incrementalDebugLog('delta', 'Deletion detected', {
            changeStart,
            changeEnd,
            lengthDiff,
            deletedChars: -lengthDiff,
            deletedText: oldText.substring(changeStart, changeEnd)
          });
        } else if (lengthDiff > 0) {
          // ✅ INSERTION: Calculate old selection by subtracting length difference
          // The selection parameter is the NEW selection (after insertion)
          // Old selection = new selection - length difference
          const newSelectionPos = Math.min(selection.anchor, selection.focus);
          const oldSelectionPos = newSelectionPos - lengthDiff;
          changeStart = Math.max(0, oldSelectionPos); // Ensure non-negative
          changeEnd = changeStart;
          incrementalDebugLog('delta', 'Insertion detected - calculated old selection', {
            newSelectionPos,
            oldSelectionPos,
            changeStart,
            changeEnd,
            lengthDiff,
            insertText: newText.substring(changeStart, changeStart + lengthDiff)
          });
        } else {
          // ✅ REPLACEMENT (same length): Use current selection as-is
          changeStart = Math.min(selection.anchor, selection.focus);
          changeEnd = Math.max(selection.anchor, selection.focus);
          incrementalDebugLog('delta', 'Replacement detected (same length)', {
            changeStart,
            changeEnd,
            replacementText: newText.substring(changeStart, changeEnd)
          });
        }

        // Dispatch delta event for UI display
        window.dispatchEvent(new CustomEvent('edgedocs:delta-calculated', { detail: { changeStart, changeEnd, lengthDiff } }));

        // ⚡ OPTIMIZATION: Skip incremental layout for large pastes (>100 chars)
        // Large pastes are faster with full re-initialization
        const PASTE_THRESHOLD = 100;
        const isPasteLikeOperation = Math.abs(lengthDiff) > PASTE_THRESHOLD;
        
        // ✅ CRITICAL FIX: If forceReinitRef is already set (from a previous large paste or other operation),
        // skip incremental layout entirely. WASM may be out of sync and applyEditDelta will fail.
        // Let the pending render cycle handle re-initialization first.
        const forceReinitPending = forceReinitRef.current;
        
        // ⚠️ DISABLED: The canvasWasmDiff check was causing issues in multi-page documents.
        // In multi-page docs, each Canvas only has its page's text (~900 chars for page 0),
        // but getLastInitializedText() returns the FULL document (~3300 chars).
        // This created a permanent ~2400 char "desync" that triggered constant re-inits.
        // 
        // The incremental layout system handles real desyncs via error recovery.
        // Large operations like paste are handled by the isPasteLikeOperation check below.
        
        // ⚡ PASTE FIX: If a re-init is pending, just update the text ref and trigger re-render
        // Don't spam warnings or trigger more re-inits - let the pending one complete
        if (forceReinitPending) {
          DEBUG_ENABLED && console.log(`⏳ [TextEditingHandler] Re-init pending, updating text ref and triggering re-render (${newText.length} chars)`);
          currentDocumentTextRef.current = newText;
          // Also update WASM's known text to prevent desync warnings on next keystroke
          if (typesetterClientRef.current && (typesetterClientRef.current as any).setLastInitializedText) {
            (typesetterClientRef.current as any).setLastInitializedText(newText);
          }
          // ✅ CRITICAL: Trigger re-render so the pasted text appears!
          lastRenderWasIncrementalRef.current = false;
          setRenderTrigger(prev => prev + 1);
          // Defer parent state update
          if (onDocumentTextChange) {
            lastLocalTextUpdateRef.current = newText;
            requestAnimationFrame(() => {
              onDocumentTextChange(newText);
            });
          }
          return; // Exit early - let the pending re-init handle it
        }

        // 🆕 PHASE 3 NEWLINE FIX: Track newline changes for logging (but don't bypass incremental layout)
        // The WASM caret_map now correctly includes entries for \n characters (fixed Oct 2025)
        // See: typesetter/PHASE_3_NEWLINE_FIX.md
        const oldNewlineCount = (oldText.match(/\n/g) || []).length;
        const newNewlineCount = (newText.match(/\n/g) || []).length;
        const lineStructureChanged = oldNewlineCount !== newNewlineCount;

        // Capture snapshot before edit for diff
        const beforeSnapshot = currentSnapshotRef?.current;

        if (isPasteLikeOperation) {
          incrementalDebugLog('incremental', `Large change detected (${Math.abs(lengthDiff)} chars) - skipping incremental layout, using full re-init`);
          incrementalLayoutSucceeded = false;
          // Force reinit to resync WASM with new text
          forceReinitRef.current = true;
          // Skip incremental layout and fall through to full re-render below
        } else {
          // ✅ INCREMENTAL LAYOUT: Now handles newlines correctly (WASM caret_map includes \n entries)
          if (lineStructureChanged) {
            incrementalDebugLog('incremental', `Line structure changed (${oldNewlineCount} → ${newNewlineCount} newlines) - using incremental layout (WASM handles this correctly now)`);
          }
          // Apply incremental text change for small edits
          incrementalDebugLog('incremental', 'Calling applyIncrementalTextChange', {
            oldTextLen: oldText.length,
            newTextLen: newText.length,
            changeStart,
            changeEnd
          });

          // ⏱️ BENCHMARK: Measure WASM round-trip time for Option C assessment
          const startTime = performance.now();
          const incrementalResult = await typesetterClientRef.current.applyIncrementalTextChange(
            oldText,
            newText,
            changeStart,
            changeEnd,
            fontFamily,
            400, // fontWeight
            'normal' // fontStyle
          );

          const duration = performance.now() - startTime;
          
          // ⏱️ BENCHMARK OUTPUT: This is the key metric for Option C viability
          // < 5ms = Excellent (Option C highly viable)
          // 5-16ms = Good (Option C viable, may need minor optimization)
          // 16-50ms = Acceptable (Option C viable with perceived lag)
          // > 50ms = Too slow (Option C needs optimistic rendering)
          perfLog('WASM applyIncrementalTextChange', duration);
          
          // Additional context for the benchmark
          if (BENCHMARK_WASM_LATENCY) {
            console.log(`   📊 Text: ${oldText.length} → ${newText.length} chars (${lengthDiff > 0 ? '+' : ''}${lengthDiff})`);
          }
          
          incrementalDebugLog('incremental', `Incremental layout completed in ${duration.toFixed(2)}ms`, {
            hasUpdatedPages: !!incrementalResult.updatedPages,
            totalPages: incrementalResult.totalPages,
            stoppedAtPage: incrementalResult.stoppedAtPage,
            cacheUpdated: incrementalResult.cacheUpdated
          });

          // ✅ CRITICAL: Update current text immediately for subsequent operations
          currentDocumentTextRef.current = newText;

          // Store the delta for potential UI display
          const delta = {
            changeStart,
            changeEnd,
            oldLength: oldText.length,
            newLength: newText.length,
            insertedText: lengthDiff > 0 ? newText.substring(changeStart, changeStart + lengthDiff) : '',
            deletedText: lengthDiff < 0 ? oldText.substring(changeStart, changeEnd) : '',
            timestamp: Date.now()
          };

          incrementalDebugLog('delta', 'Delta calculated', {
            ...delta,
            newTextPreview: newText.substring(Math.max(0, changeStart - 10), changeStart + (delta.insertedText.length || Math.abs(lengthDiff)) + 10)
          });

          // Dispatch delta event for UI display
          window.dispatchEvent(new CustomEvent('edgedocs:delta-calculated', { detail: delta }));

          // Capture snapshot before edit for diff
          const beforeSnapshot = currentSnapshotRef.current;

          if (incrementalResult.affectedParagraphIds.length > 0) {
            // Track affected paragraphs for highlighting (Phase 4)
            // Note: affectedParagraphsRef is not in our interface, skipping for now

            // Trigger overlay redraw if highlighting is enabled
            if (overlaySettingsRef.current?.highlightAffectedParagraphs) {
              try { drawOverlaysRef.current?.(); } catch {}
            }

            incrementalLayoutSucceeded = true;

            // 🆕 PHASE 3: Store incremental pages for use in renderText
            if (incrementalResult.updatedPages && incrementalResult.updatedPages.length > 0) {
              DEBUG_ENABLED && console.log('📊 [Phase3] Storing incremental pages:', {
                pagesCount: incrementalResult.updatedPages.length,
                totalPages: incrementalResult.totalPages,
                stoppedAtPage: incrementalResult.stoppedAtPage,
                spillFrontier: incrementalResult.spillFrontier
              });

              incrementalPagesRef.current = {
                pages: incrementalResult.updatedPages,
                totalPages: incrementalResult.totalPages,
                stoppedAtPage: incrementalResult.stoppedAtPage,
                timestamp: Date.now()
              };

              // ✅ PHASE 3: Use incremental pages - no full re-pagination needed
              DEBUG_ENABLED && console.log('✅ [Phase3] Incremental pagination successful - using cached pages');
              lastRenderWasIncrementalRef.current = true;
              setRenderTrigger(prev => prev + 1);
            } else if (incrementalResult.cacheUpdated) {
              // Cache was updated but no pages returned - need full pagination
              // BUT: Don't clear canvas to reduce flicker - text content is mostly the same
              DEBUG_ENABLED && console.log('⚠️ [Phase3] No incremental pages returned - fallback to full pagination (no clear)');
              incrementalPagesRef.current = null;  // Clear to force full pagination
              lastRenderWasIncrementalRef.current = true;  // Don't clear canvas - reduces flicker
              setRenderTrigger(prev => prev + 1);
            } else {
              // ⚠️ Cache not updated - fallback to full re-render but minimize flicker
              DEBUG_ENABLED && console.log('⚠️ [CanvasV3] Incremental layout succeeded but cache not updated - full re-render (no clear)');
              incrementalPagesRef.current = null;
              lastRenderWasIncrementalRef.current = true;  // Don't clear canvas
              setRenderTrigger(prev => prev + 1);
            }
          }
        }
      } catch (incrementalError) {
        console.error('⚠️ [CanvasV3] Incremental layout failed:', incrementalError);
        incrementalLayoutSucceeded = false;

        // ✅ OPTIMIZATION: Don't recover from "Worker not initialized" errors
        // This just means the worker needs to be initialized, not that it's corrupted
        const isNotInitializedError = String(incrementalError).includes('not initialized');

        if (!isNotInitializedError) {
          // Execute systematic WASM error recovery for real errors
          const recoveryResult = await executeWasmErrorRecovery(incrementalError, {
            typesetterClientRef,
            consecutiveWasmErrorsRef,
            setTypesetterClient,
            setIsTypesetterReady,
            forceReinitRef
          });

          if (!recoveryResult.shouldRetry) {
            console.error('🚫 [CanvasV3] WASM recovery failed - aborting operation');
            return; // Don't continue with fallback rendering
          }
        } else {
          DEBUG_ENABLED && console.log('✅ [CanvasV3] Worker not initialized - skipping recovery, will fall back to full render');
        }
      }
    }

    // FALLBACK: Full re-render when incremental layout fails or isn't available
    if (!incrementalLayoutSucceeded) {
      DEBUG_ENABLED && console.log('🔄 [CanvasV3] Incremental layout failed or not available - performing full re-render');

      // ✅ CRITICAL: Update current text immediately for renderText
      currentDocumentTextRef.current = newText;

      // Force reinit to resync WASM with new text
      forceReinitRef.current = true;
      lastRenderWasIncrementalRef.current = false; // Full re-render
      setRenderTrigger(prev => prev + 1);
    }

    // ✅ PART 2 OPTION A: Defer parent state update until after render cycle completes
    // This prevents parent re-render from interfering with current render cycle
    // The Canvas will render using currentDocumentTextRef (which is already updated)
    // and the parent state will update after rendering completes
    if (onDocumentTextChange) {
      DEBUG_ENABLED && console.log('📝 [CanvasV3] Scheduling deferred parent state update:', {
        textLength: newText.length,
        incrementalLayoutSucceeded,
        textPreview: newText.substring(0, 50)
      });
      
      // ✅ CRITICAL: Set lastLocalTextUpdateRef BEFORE deferred update
      // This ensures setupStableInputTextSync knows this is our update, not external
      lastLocalTextUpdateRef.current = newText;
      
      // Defer parent update until after current render cycle completes
      // This ensures Canvas renders with currentDocumentTextRef before parent state updates
      // Using requestAnimationFrame ensures it runs after the browser's paint cycle
      requestAnimationFrame(() => {
        // Use another requestAnimationFrame to ensure it runs after React's render
        requestAnimationFrame(() => {
          DEBUG_ENABLED && console.log('📝 [CanvasV3] Executing deferred parent state update:', {
            textLength: newText.length,
            textPreview: newText.substring(0, 50)
          });
          onDocumentTextChange(newText);
        });
      });
    } else {
      // This can happen for pages > 0 (only page 0 gets the callback) or during initial mount
      // Only warn once per session to avoid console spam
      if (!(window as any).__edgedocs_warned_no_callback) {
        console.warn('📝 [CanvasV3] No onDocumentTextChange callback provided - text changes will not persist to React state. This is expected for pages > 0.');
        (window as any).__edgedocs_warned_no_callback = true;
      }
    }

    // ✅ CRITICAL: Update stable input text to prevent cursor jumping
    // This prevents useEdgeDocsInput from resetting when React state updates
    setStableInputText(prev => {
      if (prev !== newText) {
        debugLog('📝 [CanvasV3] Updating stableInputText for external change:', {
          oldLength: prev.length,
          newLength: newText.length,
          isInitialLoad: prev === ''
        });
        return newText;
      }
      return prev;
    });

    // 🚀 RENDERING OPTIMIZATION: Only trigger full re-render when incremental layout fails
    // This prevents expensive full document re-layout on every keystroke
    const shouldTriggerFullRerender = !incrementalLayoutSucceeded;
    if (shouldTriggerFullRerender) {
      DEBUG_ENABLED && console.log('🔄 [CanvasV3] Triggering full re-render (incremental layout failed)');
      lastRenderWasIncrementalRef.current = false; // Full re-render
      setRenderTrigger(prev => prev + 1);
    } else {
      DEBUG_ENABLED && console.log('🔄 [CanvasV3] Using incremental layout - no full re-render needed');
    }

    // 🐛 Trigger dashboard update after text changes to show lag
    try { renderOverlayRef.current?.(); } catch {}
  };
}
